import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@ui/lib/utils';

enum ButtonVariant {
  DEFAULT = 'default',
  OUTLINE = 'outline',
  SECONDARY = 'secondary',
  GHOST = 'ghost',
  DESTRUCTIVE = 'destructive',
  LINK = 'link',
}

const buttonVariants = cva(
  cn(
    'group/button inline-flex shrink-0 items-center justify-center border border-transparent',
    'bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none',
    'focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50',
    'cursor-pointer active:not-aria-[haspopup]:translate-y-px',
    'disabled:cursor-not-allowed disabled:pointer-events-none disabled:opacity-50',
    'aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20',
    'dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40',
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
  ),
  {
    variants: {
      variant: {
        [ButtonVariant.DEFAULT]: cn(
          'bg-primary text-primary-foreground',
          'hover:bg-[color-mix(in_oklch,var(--primary),var(--foreground)_12%)]'
        ),
        [ButtonVariant.OUTLINE]: cn(
          'border-border bg-background hover:bg-muted hover:text-foreground',
          'aria-expanded:bg-muted aria-expanded:text-foreground',
          'dark:border-input dark:bg-input/30 dark:hover:bg-input/50'
        ),
        [ButtonVariant.SECONDARY]: cn(
          'bg-secondary text-secondary-foreground',
          'hover:bg-[color-mix(in_oklch,var(--secondary),var(--foreground)_5%)]',
          'aria-expanded:bg-secondary aria-expanded:text-secondary-foreground'
        ),
        [ButtonVariant.GHOST]: cn(
          'hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground',
          'dark:hover:bg-muted/50'
        ),
        [ButtonVariant.DESTRUCTIVE]: cn(
          'bg-destructive/10 text-destructive hover:bg-destructive/20',
          'focus-visible:border-destructive/40 focus-visible:ring-destructive/20',
          'dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40'
        ),
        [ButtonVariant.LINK]: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
        xs: cn(
          'h-6 gap-1 px-2 text-xs',
          'has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5',
          "[&_svg:not([class*='size-'])]:size-3"
        ),
        sm: cn(
          'h-7 gap-1 px-2.5 text-[0.8rem]',
          'has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5',
          "[&_svg:not([class*='size-'])]:size-3.5"
        ),
        lg: 'h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
        icon: 'size-8',
        'icon-xs': cn('size-6', "[&_svg:not([class*='size-'])]:size-3"),
        'icon-sm': 'size-7',
        'icon-lg': 'size-9',
      },
      shape: {
        pill: 'rounded-full',
        rounded: 'rounded-lg',
        square: 'rounded-none',
      },
    },
    defaultVariants: {
      variant: ButtonVariant.DEFAULT,
      size: 'default',
      shape: 'pill',
    },
  }
);

type ButtonProps = ButtonPrimitive.Props &
  Omit<VariantProps<typeof buttonVariants>, 'variant'> & { variant?: ButtonVariant };

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = ButtonVariant.DEFAULT, size = 'default', shape = 'pill', ...props }, ref) => (
    <ButtonPrimitive
      ref={ref}
      data-slot="button"
      className={cn(buttonVariants({ variant, size, shape, className }))}
      {...props}
    />
  )
);
Button.displayName = 'Button';

export { Button, ButtonVariant, buttonVariants };
export type { ButtonProps };
